import React from 'react'
import PropTypes from 'prop-types'
import { Grid, Divider, TextField, FormControl, Typography } from '@material-ui/core'
import AutocompleteNative, { createFilterOptions } from '@material-ui/lab/Autocomplete'

import { CustomButton, ModalConfirmacao, SecaoGenerica } from 'components'
import { useFormLocal } from './hooks/useFormLocal'
import useStyles from './Form.styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SaveIcon from '@material-ui/icons/Save'
import DeleteIcon from '@material-ui/icons/Delete'

// Limitar renderização no Autocomplete para evitar lentidão no DOM (Boas Práticas de Performance 🛸🚀)
const filterOptions = createFilterOptions({
  limit: 100,
})

export const Form = (props) => {
  const { local, veiculo } = props
  const classes = useStyles()

  const {
    values,
    cidades,
    processando,
    openAlertDelete,
    setOpenAlertDelete,
    openMirror,
    setOpenMirror,
    cep,
    errors,
    isFormValid,
    handleCepChange,
    handleSearchCep,
    handleChange,
    handleAutocompleteChange,
    handleConfirm,
    handleDelete,
    handleCancelar,
    handleCoordinateInput,
    router,
  } = useFormLocal(props)

  return (
    <div className={classes.root}>
      {/* Seção 1: Pesquisa por CEP */}

      <Grid container alignItems="center" spacing={2}>
        <Grid item md={3} sm={6} xs={12} className={classes.item}>
          <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
            <TextField
              id="cep"
              fullWidth
              label="CEP"
              name="cep"
              value={cep}
              onChange={handleCepChange}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </FormControl>
        </Grid>
        <Grid item md={3} sm={6} xs={12} className={classes.item}>
          <CustomButton colorType="primary" onClick={handleSearchCep} disabled={processando}>
            Pesquisar
          </CustomButton>
        </Grid>
      </Grid>


      <Divider className={classes.dividerBarraPesquisa} />

      {/* Seção 2: Dados do Endereço */}
      <SecaoGenerica titulo="Dados do Endereço">
        <Grid container spacing={2}>
          <Grid className={classes.item} item md={5} sm={5} xs={12}>
            <AutocompleteNative
              filterOptions={filterOptions}
              value={values.municipio || null}
              name="municipio"
              noOptionsText="Sem informações"
              options={cidades}
              getOptionSelected={(option, value) => option.nome === value.nome}
              onChange={(event, newValue) => {
                handleAutocompleteChange('municipio', newValue)
              }}
              getOptionLabel={(option) => `${option.nome}`}
              renderOption={(option) => `${option.nome}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="CIDADE"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  error={Boolean(errors.municipio)}
                  helperText={errors.municipio}
                />
              )}
            />
          </Grid>

          <Grid className={classes.item} item md={5} sm={5} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="logradouro"
                error={Boolean(errors.logradouro)}
                fullWidth
                helperText={errors.logradouro}
                inputProps={{
                  maxLength: 75,
                }}
                label="LOGRADOURO"
                name="logradouro"
                onChange={handleChange}
                value={values.logradouro || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </FormControl>
          </Grid>

          <Grid className={classes.item} item md={2} sm={2} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="numero"
                fullWidth
                inputProps={{
                  maxLength: 6,
                }}
                label="NÚMERO"
                name="numero"
                onChange={handleChange}
                value={values.numero || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Grid>

          <Grid className={classes.item} item md={6} sm={6} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="bairro"
                error={Boolean(errors.bairro)}
                fullWidth
                helperText={errors.bairro}
                label="BAIRRO"
                name="bairro"
                onChange={handleChange}
                value={values.bairro || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </FormControl>
          </Grid>

          <Grid className={classes.item} item md={6} sm={6} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="complemento"
                fullWidth
                inputProps={{
                  maxLength: 100,
                }}
                label="COMPLEMENTO"
                name="complemento"
                onChange={handleChange}
                value={values.complemento || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </SecaoGenerica>

      <Divider className={classes.dividerBarraPesquisa} />

      {/* Seção 3: Coordenadas Geográficas (GPS) */}
      <SecaoGenerica titulo="Coordenadas Geográficas (GPS)">
        <Grid container spacing={2}>
          <Grid className={classes.item} item md={6} sm={6} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="latitude"
                onInput={handleCoordinateInput}
                fullWidth
                label="LATITUDE"
                name="latitude"
                onChange={handleChange}
                value={values.latitude || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Grid>

          <Grid className={classes.item} item md={6} sm={6} xs={12}>
            <FormControl style={{ minWidth: '100%', maxWidth: '100%' }}>
              <TextField
                id="longitude"
                onInput={handleCoordinateInput}
                fullWidth
                label="LONGITUDE"
                name="longitude"
                onChange={handleChange}
                value={values.longitude || ''}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </SecaoGenerica>

      <Divider className={classes.dividerBotoes} />

      {/* Ações inferiores de submissão do formulário */}
      <div className={classes.botoesFormulario}>
        {props.setOpenFicha ? (
          <CustomButton onClick={handleCancelar} variant="outlined" colorType="primary" disabled={processando}>
            Cancelar
          </CustomButton>
        ) : (
          <CustomButton variant="outlined" colorType="primary" startIcon={<ArrowBackIcon />} onClick={() => router.history.goBack()} disabled={processando}>
            Voltar
          </CustomButton>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {local?.id > 0 && !veiculo && (
            <CustomButton onClick={() => setOpenAlertDelete(true)} variant="contained" colorType="error" disabled={processando} startIcon={<DeleteIcon />}>
              Excluir
            </CustomButton>
          )}
          <CustomButton variant="contained" colorType="secondary" onClick={() => setOpenMirror(true)} disabled={!isFormValid || processando} startIcon={<SaveIcon />}>
            {veiculo ? 'Salvar' : local?.id > 0 ? 'Editar local' : 'Cadastrar local'}
          </CustomButton>
        </div>
      </div>

      {/* Modal de Confirmação Espelho (Dados de Salvamento) */}
      <ModalConfirmacao
        aberto={openMirror}
        onClose={() => setOpenMirror(false)}
        onConfirm={handleConfirm}
        processando={processando}
        titulo="CONFIRMAÇÃO DOS DADOS PREENCHIDOS"
        confirmarLabel={veiculo ? 'Salvar' : local?.id > 0 ? 'Salvar' : 'Cadastrar'}
        cancelarLabel="Cancelar"
        maxWidth="md"
      >
        <Grid container spacing={3} className={classes.mirrorContainer}>


          <Grid item md={6} sm={12} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>Endereço</Typography>
            <Typography className={classes.mirrorValue}>
              {values.logradouro ? `${values.logradouro}, ${values.numero || 'S/N'}, ${values.bairro}` : 'Não informado'}
            </Typography>
          </Grid>

          <Grid item md={6} sm={12} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>Cidade/Estado</Typography>
            <Typography className={classes.mirrorValue}>
              {values.municipio ? `${values.municipio.nome} / ${values.estado || 'RN'}` : 'Não informado'}
            </Typography>
          </Grid>

          <Grid item md={6} sm={12} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>CEP</Typography>
            <Typography className={classes.mirrorValue}>
              {values.cep || 'Não informado'}
            </Typography>
          </Grid>

          <Grid item md={6} sm={12} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>Complemento</Typography>
            <Typography className={classes.mirrorValue}>
              {values.complemento || 'Não informado'}
            </Typography>
          </Grid>

          <Grid item md={6} sm={6} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>Latitude</Typography>
            <Typography className={classes.mirrorValue}>
              {values.latitude || 'Não informado'}
            </Typography>
          </Grid>

          <Grid item md={6} sm={6} xs={12} className={classes.mirrorField}>
            <Typography className={classes.mirrorLabel}>Longitude</Typography>
            <Typography className={classes.mirrorValue}>
              {values.longitude || 'Não informado'}
            </Typography>
          </Grid>
        </Grid>
      </ModalConfirmacao>

      {/* Modal de Confirmação de Exclusão */}
      <ModalConfirmacao
        aberto={openAlertDelete}
        onClose={() => setOpenAlertDelete(false)}
        onConfirm={handleDelete}
        processando={processando}
        titulo="Tem certeza que deseja deletar?"
        confirmarLabel="Confirmar"
        cancelarLabel="Cancelar"
        confirmarColor="error"
      >
        <Typography variant="body1">
          O local não poderá mais ser acessado. Tem certeza de que deseja realizar esta operação?
        </Typography>
      </ModalConfirmacao>
    </div>
  )
}

Form.propTypes = {
  local: PropTypes.object,
  veiculo: PropTypes.bool,
  setLocalAtualizado: PropTypes.func,
  idCaso: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setOpenFicha: PropTypes.func,
  openFicha: PropTypes.bool,
}
